import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { LuArrowUpDown } from "react-icons/lu";
import "../assets/css/styles.css";
import Popover from "./popover";
import axiosInstance from "../services/axiosInstance";
import { encrypt } from "../utils/cryptoUtils";
import { PropagateLoader } from "react-spinners";
import { OrderStatusNames, OrderTypeNameEnum } from "../enums/OrderEnum";
import { width } from "@mui/system";

const SortIcon = ({ sortDirection }) => {
  if (sortDirection === "asc") {
    return (
      <LuArrowUpDown className="ml-1 !w-4 !h-5" style={{ color: "#EFEFEF" }} />
    );
  }
  if (sortDirection === "desc") {
    return (
      <LuArrowUpDown className="ml-1 !w-4 !h-5" style={{ color: "#EFEFEF" }} />
    );
  }
  return (
    <LuArrowUpDown
      className="ml-1 !w-4 !h-5"
      style={{ color: "#EFEFEF", opacity: "1" }}
    />
  );
};

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#376EA4",
      color: "#FAFDFF",
      fontSize: "16px",
      fontFamily: "InterBold",
      textAlign: "left",
      padding: "15px",
      opacity: "1",
      "@media screen and (min-width: 1250px) and (max-width: 1600px)": {
        fontSize: "14px",
      },
    },
    hoverStyle: {
      color: "#FAFDFF",
      opacity: "1",
    },
  },
  rows: {
    style: {
      fontSize: "16px",
      color: "#828282",
      padding: "6px",
      backgroundColor: "rgba(252, 252, 252, 0.5)",
      borderBottom: "1px solid rgba(239, 239, 239, 0.8)",
      borderTop: "1px solid rgba(239, 239, 239, 0.8)",
      //overflow: "hidden",
      "@media screen and (min-width: 1250px) and (max-width: 1600px)": {
        fontSize: "14px",
        padding: "4px",
      },
    },
    highlightOnHoverStyle: {
      fontSize: "16px",
      color: "#202020",
      padding: "6px",
      backgroundColor: "#F4F9FF",
      borderBottom: "1px solid rgba(239, 239, 239, 0.8)",
      borderTop: "1px solid rgba(239, 239, 239, 0.8)",
      "@media screen and (min-width: 1250px) and (max-width: 1600px)": {
        fontSize: "14px",
        padding: "4px",
      },
    },
  },
  table: {
    style: {
      display: "block",
      maxHeight: "calc(100vh - 280px)",
      height: "fit-content",
      overflowY: "auto",
      borderCollapse: "separate",
      borderSpacing: 0,
      "@media screen and (min-width: 1600px)": {
        maxHeight: "calc(100vh - 300px)",
        overflowX: "hidden",
      },
      "@media screen and (min-width: 760px) and (max-width: 1250px)": {
        maxHeight: "calc(100vh - 260px)",
        overflowX: "hidden",
      },
      "@media screen and (min-width: 768px) and (max-width: 1024px)": {
        maxHeight: "calc(100vh - 320px)",
        overflowX: "hidden",
      },
    },
  },
};

const formatDueDeliveryDate = (dueDeliveryDate) => {
  if (!dueDeliveryDate) return "";

  const date = new Date(dueDeliveryDate);

  // Extract day, month, hours, and minutes
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  // Format as dd.MM | HH:mm
  return `${day}.${month} | ${hours}:${minutes}`;
};

const TableList = ({ searchTerm }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  const columns = [
    {
      name: "Praxis",
      selector: (row) => row.dentistName,
      sortable: true,
      sortFunction: (a, b) => a.dentistName.localeCompare(b.dentistName),
      width: "20%",
    },
    {
      name: "Patient",
      selector: (row) => row?.patientName,
      sortable: true,
      sortFunction: (a, b) => a?.patientName?.localeCompare(b?.patientName),
      width: "15%",
    },
    {
      name: "Auftragsart",
      selector: (row) => row.orderTypeId,
      sortable: true,
      format: (row) => (row.orderTypeId === "" ? "" : row.orderTypeId),
      sortFunction: (a, b) => a.orderTypeId.localeCompare(b.orderTypeId),
      width: "20%",
    },
    {
      id: "dueDeliveryColumn",
      name: "Eingang bis",
      selector: (row) => new Date(row.dueDeliveryRaw),
      sortable: true,
      format: (row) => row.dueDelivery,
      sortFunction: (a, b) =>
        new Date(a.dueDeliveryRaw) - new Date(b.dueDeliveryRaw),
      width: "15%",
    },
    {
      name: "Liefertermin",
      selector: (row) => new Date(row.finalDeliveryRaw),
      sortable: true,
      format: (row) => row.finalDelivery,
      width: "15%",
    },
    {
      name: "Ausschreiben",
      selector: (row) => row.create,
      sortable: false,
      width: "15%",
      cell: (row) => (
        <Popover
          trigger="hover"
          content={
            <p
              className="absolute -right-5 text-blue-400 px-2 py-0.5 3xl:py-1 leading-5 bg-blue rounded w-auto  -top-7 text-lg xl:text-xs 3xl:text-lg whitespace-nowrap after:content-[' '] after:-bottom-2 after:right-8  after:absolute after:border after:border-l-8 after:border-r-8 after:border-transparent after:w-0 after:h-0 after:m-auto after:border-t-[10px] after:border-t-blue shadow-[2px_2px_4px_1px_rgba(0,0,0,0.2)] max-w-fit"
              style={{}}
            >
              Auftrag Anlegen
            </p>
          }
        >
          <button
            onClick={() => handleCreateButtonClick(row)}
            className="rounded-lg p-1.5 3xl:p-2 my-1 border hover:border-blue-600 border-transparent shadow-[0_0_4px_rgba(55,110,164,0.2)]"
            style={{
              backgroundColor: "rgba(201, 226, 252, 0.3)",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            <img src="./icons/add_box.svg" />
          </button>
        </Popover>
      ),
      ignoreRowClick: true,
      allowoverflow: true,
      button: true,
    },
  ];

  const navigate = useNavigate();

  const handleCreateButtonClick = (row) => {
    const encryptedId = encrypt(row.id.toString());
    navigate(`/order-creation?orderId=${encryptedId}`);
  };
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get("/api/order/getPPOrders");
        if (response) {
          if (Array.isArray(response)) {
            const mappedOrders = response.map((order) => {
              // const dueDeliveryDate = new Date(order.dueDeliveryDate);
              // const finalDeliveryDate = new Date(order.deliveryDate);
              // const isDue = dueDeliveryDate > finalDeliveryDate;
              return {
                id: order.orderId,
                dentistName: order.dentistName ? order.dentistName : "",
                patientName: order.patientName,
                dueDelivery: formatDueDeliveryDate(order.dueDeliveryDate),
                dueDeliveryRaw: order.dueDeliveryDate,
                finalDelivery: formatDueDeliveryDate(order.deliveryDate),
                finalDeliveryRaw: order.deliveryDate,
                orderTypeId: OrderTypeNameEnum[order.orderTypeId],
                isDue:
                  new Date(order.deliveryDate) <
                  new Date(order.dueDeliveryDate),
                create: "",
              };
            });
            setOrders(mappedOrders);
          } else {
            setOrders([]);
          }
        } else {
          setOrders([]);
        }
      } catch (error) {
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const filteredOrders = React.useMemo(() => {
    const clonedOrders = [...orders];
    return clonedOrders.filter(
      (order) =>
        order.dentistName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.patientName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [orders, searchTerm]);

  return (
    <div className="relative">
      {isLoading ? (
        // Loader Overlay
        <div className="flex justify-center items-center min-h-60">
          <PropagateLoader color="rgb(55 110 164 / var(--tw-bg-opacity, 1))" />
        </div>
      ) : (
        <DataTable
          defaultSortFieldId="dueDeliveryColumn"
          defaultSortAsc={true}
          noDataComponent={
            <div className="py-4 text-gray-500">
              Es sind keine Datensätze zum Anzeigen vorhanden
            </div>
          }
          className=" mt-7 !rounded-xl shadow-[0_0_8px_rgba(44,99,152,0.2)] font-interRegular"
          scrollable
          scrollHeight="400px"
          columns={columns}
          data={filteredOrders}
          pagination={false}
          fixedHeader
          customStyles={customStyles}
          sortIcon={<SortIcon />}
          highlightOnHover
          conditionalRowStyles={[
            {
              when: (row) => row.isDue,
              style: {
                backgroundColor: "#FE725C33",
                color: "#828282",
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default TableList;
